import { useQuery } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import { FIELD_MAX_LENGTHS, VALIDATION_MESSAGES } from 'shared'
import { getAllTags } from '@/lib'
import { queryKeys } from '@/lib/queryKeys'
import type { TagValue } from '../types'
import { parseTagString, tagToString } from '../utils'

interface UseTagSelectParams {
  value: TagValue[]
  onChange: (tags: TagValue[]) => void
  allowCreate: boolean
  collapsible: boolean
}

export function useTagSelect({ value, onChange, allowCreate, collapsible }: UseTagSelectParams) {
  const [searchValue, setSearchValue] = useState('')
  const [isOpen, setIsOpen] = useState(!collapsible)

  // 全タグを取得（オートコンプリート用）
  const { data: allTagsData } = useQuery({
    queryKey: queryKeys.allTags,
    queryFn: async () => {
      return getAllTags({ tagonly: true })
    },
  })

  // タグを追加
  const handleAddTag = useCallback(
    (tagStr: string) => {
      // 文字数バリデーション
      if (tagStr.length > FIELD_MAX_LENGTHS.NAME_SHORT) {
        return
      }

      const newTag = parseTagString(tagStr)
      const tagKey = tagToString(newTag)
      // 重複チェック
      const exists = value.some((t) => tagToString(t) === tagKey)
      if (!exists) {
        onChange([...value, newTag])
      }
      setSearchValue('')
      if (collapsible) {
        setIsOpen(false)
      }
    },
    [value, onChange, collapsible],
  )

  // タグを削除
  const handleRemoveTag = useCallback(
    (tagToRemove: TagValue) => {
      const tagKey = tagToString(tagToRemove)
      onChange(value.filter((t) => tagToString(t) !== tagKey))
    },
    [value, onChange],
  )

  // 値加工（trim）→ バリデーションの順で判定する（handleAddTag と同じ順序）
  const trimmedSearchValue = searchValue.trim()
  const hasInputError = trimmedSearchValue.length > FIELD_MAX_LENGTHS.NAME_SHORT

  // Enterキーで新規タグを追加
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && trimmedSearchValue && allowCreate && !hasInputError) {
        e.preventDefault()
        handleAddTag(trimmedSearchValue)
      }
    },
    [trimmedSearchValue, allowCreate, handleAddTag, hasInputError],
  )

  // 選択済みタグのキーセット
  const selectedTagKeys = new Set(value.map(tagToString))

  // フィルタリングされたタグ候補
  // isReadonly=trueのタグは候補一覧に表示しない
  const filteredTags = (allTagsData?.tags || []).filter(
    (tag) => tag?.name && !selectedTagKeys.has(tag.name) && !tag.isReadonly,
  )

  /**
   * タグが読み取り専用かどうかを判定
   * masterTags（allTagsData）からidで検索してisReadonlyを確認
   */
  const isReadonlyTag = useCallback(
    (tag: TagValue): boolean => {
      if (!tag || !tag.id) return false
      const masterTag = allTagsData?.tags.find((t) => t.id === tag.id)
      if (!masterTag) return false
      return !!masterTag.isReadonly
    },
    [allTagsData],
  )

  // フォーカス・ブラーハンドラ
  const handleFocus = collapsible ? () => setIsOpen(true) : undefined

  const handleBlur = collapsible
    ? (event: React.FocusEvent) => {
        const next = event.relatedTarget as HTMLElement | null
        if (next?.closest('[cmdk-root]')) {
          return
        }
        setIsOpen(false)
      }
    : undefined

  // 入力値のエラーメッセージ
  const inputError = hasInputError ? VALIDATION_MESSAGES.maxLength(FIELD_MAX_LENGTHS.NAME_SHORT) : null

  return {
    searchValue,
    setSearchValue,
    isOpen,
    filteredTags,
    handleAddTag,
    handleRemoveTag,
    handleKeyDown,
    handleFocus,
    handleBlur,
    isReadonlyTag,
    inputError,
  }
}
